import { useQueryClient } from '@tanstack/react-query'
import {
  Checkbox,
  Divider,
  Popover,
  type RefSelectProps,
  Segmented,
  Select
} from 'antd'
import clsx from 'clsx'
import { useAtomValue } from 'jotai'
import {
  forwardRef,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import toast from 'react-hot-toast'
import { FaBullhorn, FaEdit, FaLink, FaPlus } from 'react-icons/fa'

import { Button } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import {
  ACTIONS,
  CONFIRMATION_MESSAGES,
  DESCRIPTIONS,
  ERROR_MESSAGES,
  LABELS,
  MENU,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { configQueryKeys } from '@/features/config/query-keys'
import { updateCustomerSiteGroupCode } from '@/features/customers/api'
import { useCustomersSelectQuery } from '@/features/customers/hooks'
import { customersQueryKeys } from '@/features/customers/query-keys'
import { SelectOptionWithAction } from '@/features/shared/components'
import {
  isSiteAlreadyLinkedToCustomer,
  resolveSiteGroupCode,
  toastApiErrorMessage
} from '@/features/shared/utils'
import { vehicleDispatchBoardsQueryKeys } from '@/features/vehicle-dispatch-boards/query-keys'
import { useAntdModal } from '@/libs/antd/hooks'
import {
  type SiteExEntity,
  type SiteSelectListExEntity,
  type SiteWithAreaEntity
} from '@/libs/api'
import {
  useSelectExcludeNotExistValues,
  useSelectOnChangeHandler
} from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'
import { cn } from '@/utils/tailwind.utils'

import { updateSiteSiteGroupCode, type UpdateSiteRequest } from '../../api'
import { useSitesWithAreaQuery } from '../../hooks/use-site-with-area-query'
import { sitesQueryKeys } from '../../query-keys'
import { SiteModal } from '../SiteModal'
import { mapToSiteFieldName } from '../SiteSelect'

import styles from './SiteSelectWithLocation.module.scss'

type Props = SingleOrMultipleSelectProps<number, SiteSelectListExEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcId?: number
  disabledIds?: number[]
  onCreate?: (site: SiteExEntity) => void
  selectedAreaId?: number | null
  /** 荷主ID指定時、「おすすめ」「すべて」タブを表示する */
  customerId?: number | null
  /**
   * 候補行に荷主との「関連付ける」ボタンを表示するか（#3669）。
   * customerId の有無だけで自動判定せず明示的に有効化する。発地・着地・経由地では
   * 有効化する（PdM要望、#3669）。荷主選択の概念自体が無いフォーム（非配送タブ等）は
   * customerId が渡らないため、このフラグの値に関わらず表示されない
   */
  enableCustomerLink?: boolean
}

/** おすすめ / すべて 切替の値 */
const SITE_TAB_RECOMMENDED = 'recommended' as const
const SITE_TAB_ALL = 'all' as const
type SiteTab = typeof SITE_TAB_RECOMMENDED | typeof SITE_TAB_ALL

/**
 * 地点を選択するSelect（住所の表示・絞り込み対応）
 */
export const SiteSelectWithLocation = forwardRef(
  (
    {
      tenantKey,
      dcId,
      className,
      placeholder = DESCRIPTIONS.SELECT,
      value,
      mode,
      disabledIds,
      onChange,
      onCreate,
      selectedAreaId,
      customerId,
      enableCustomerLink,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [isOpenModal, setIsOpenModal] = useState(false)
    const [updateRequest, setUpdateRequest] =
      useState<UpdateSiteRequest | null>(null)
    const [showAreas, setShowAreas] = useState(true)
    const [showSites, setShowSites] = useState(true)
    const [modal, modalContext] = useAntdModal()

    // おすすめ / すべて タブ状態（荷主が設定済み siteGroupCode を持つ場合のみ有効）
    // 荷主未選択、または荷主の siteGroupCode が未設定(null/空文字)のときはタブ非表示
    // （関連地点が定義されていない荷主で「関連地点」タブを出さない #2423）
    const hasCustomerId = customerId !== undefined && customerId !== null
    // customerId propが渡されているか（荷主選択の概念があるフォームか）。未選択(null)とは区別する（#3669）
    const hasCustomerSelection = customerId !== undefined
    const { data: customers } = useCustomersSelectQuery(tenantKey, {
      disabled: !hasCustomerId
    })
    const selectedCustomer = hasCustomerId
      ? customers?.find((c) => c.id === customerId)
      : undefined
    // SiteModalへ渡す荷主。フォームに荷主選択の概念自体が無い場合はundefined、
    // 概念はあるが未選択の場合はnullを渡し、地点編集モーダル側で表示を切り替える（#3669）
    const siteModalCustomer = hasCustomerSelection
      ? (selectedCustomer ?? null)
      : undefined
    const hasSiteGroupCode =
      selectedCustomer?.siteGroupCode != null &&
      selectedCustomer.siteGroupCode !== ''
    const canUseRecommendedTab = hasCustomerId && hasSiteGroupCode
    const [siteTab, setSiteTab] = useState<SiteTab>(SITE_TAB_RECOMMENDED)

    // 荷主が変更されたらタブを「おすすめ」にリセット
    useEffect(() => {
      if (canUseRecommendedTab) {
        setSiteTab(SITE_TAB_RECOMMENDED)
      }
    }, [customerId, canUseRecommendedTab])

    const selectedDcId = useAtomValue(selectedDcIdAtom)
    if (dcId === undefined) {
      // 拠点IDが指定されていない場合は、選択された拠点IDを使用して絞り込む
      dcId = selectedDcId
    }

    // 「すべて」の地点データ（常に取得）
    const {
      data: allSites,
      isFetching: isFetchingAll,
      refetch: refetchAll
    } = useSitesWithAreaQuery({
      tenantKey,
      dcId,
      areaId: selectedAreaId
    })

    // 「おすすめ」の地点データ（荷主が siteGroupCode を持つ場合のみ取得）
    const {
      data: recommendedSites,
      isFetching: isFetchingRecommended,
      refetch: refetchRecommended
    } = useSitesWithAreaQuery(
      {
        tenantKey,
        dcId,
        areaId: selectedAreaId,
        customerId: customerId ?? undefined
      },
      { disabled: !canUseRecommendedTab }
    )

    // 選択中 value に関連地点外の地点/エリアが含まれる場合、デフォルトタブを「すべて」に切替
    // （例: 履歴や外部からの初期値で関連地点に含まれない地点が選択されたケース）
    // 一致時は現在のタブ選択を尊重（ユーザー操作で ALL に切り替えたのを戻さない）
    useEffect(() => {
      if (!canUseRecommendedTab) return
      if (!recommendedSites) return
      const selectedIds =
        value === undefined || value === null
          ? []
          : Array.isArray(value)
            ? value
            : [value]
      if (selectedIds.length === 0) return
      const allInRecommended = selectedIds.every(
        (id) => id == null || recommendedSites.some((s) => s.id === id)
      )
      if (!allInRecommended) setSiteTab(SITE_TAB_ALL)
    }, [canUseRecommendedTab, recommendedSites, value])

    // タブに応じて表示するデータを切替
    const sites = useMemo(() => {
      return canUseRecommendedTab && siteTab === SITE_TAB_RECOMMENDED
        ? recommendedSites
        : allSites
    }, [canUseRecommendedTab, siteTab, recommendedSites, allSites])

    const isFetching =
      canUseRecommendedTab && siteTab === SITE_TAB_RECOMMENDED
        ? isFetchingRecommended
        : isFetchingAll

    // エリアと地点に分割
    const [sitesWithArea, sitesWithoutArea] = useMemo(() => {
      const withArea: typeof sites = []
      const withoutArea: typeof sites = []

      sites?.forEach((site) => {
        if (site.isArea) withArea.push(site)
        else withoutArea.push(site)
      })

      return [withArea, withoutArea]
    }, [sites])

    // チェックボックスの状態に応じて変数を選択
    // おすすめタブではフィルタを適用しない（チェックボックスが非表示のため）
    const isRecommendedTab =
      canUseRecommendedTab && siteTab === SITE_TAB_RECOMMENDED
    const filteredSites = useMemo(() => {
      if (isRecommendedTab) return sites ?? []
      if (showSites && showAreas) return sites ?? []
      if (showSites && !showAreas) return sitesWithoutArea
      if (!showSites && showAreas) return sitesWithArea
      return [] // どちらもチェックなし
    }, [
      sites,
      sitesWithArea,
      sitesWithoutArea,
      showSites,
      showAreas,
      isRecommendedTab
    ])

    const options = useMemo(() => {
      const baseOptions = filteredSites.map((site) => ({
        ...site,
        // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
        label: mapToSiteFieldName(site),
        disabled: disabledIds?.includes(site.id),
        address: site.location
      }))
      // 選択中の値が現在のタブ（関連地点）に含まれない場合、ラベル表示のため allSites から補完。
      // 例: 履歴から非関連地点が origin/dest に入ったとき、Select が option 不在で
      //     生の ID (例「1193」) を表示してしまうのを防ぐ
      if (value === undefined || value === null || !allSites) return baseOptions
      const selectedIds = Array.isArray(value) ? value : [value]
      const missingIds = selectedIds.filter(
        (id) => id != null && !baseOptions.some((o) => o.id === id)
      )
      if (missingIds.length === 0) return baseOptions
      const supplementalOptions = allSites
        .filter((s) => missingIds.includes(s.id))
        .map((site) => ({
          ...site,
          label: mapToSiteFieldName(site),
          disabled: disabledIds?.includes(site.id),
          address: site.location
        }))
      return [...baseOptions, ...supplementalOptions]
    }, [filteredSites, disabledIds, value, allSites])

    const queryClient = useQueryClient()

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const handleCreateAndUpdateSuccess = useCallback(
      async (site: SiteExEntity) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['sitesWithArea']
          }),
          queryClient.invalidateQueries({
            queryKey: configQueryKeys.sites(dcId)
          }),
          queryClient.invalidateQueries({
            queryKey: vehicleDispatchBoardsQueryKeys.all
          }),
          // 荷主の関連地点関連付けコードを更新した場合に備え、荷主一覧も再取得する（#3669）
          queryClient.invalidateQueries({
            queryKey: customersQueryKeys.all
          }),
          // 地点側の関連地点関連付けコードを候補行の「関連付ける」ボタンで
          // 更新した場合に備え、地点一覧キャッシュも再取得する（#3669）
          queryClient.invalidateQueries({
            queryKey: sitesQueryKeys.all
          })
        ])
        await Promise.all([
          refetchAll(),
          ...(canUseRecommendedTab ? [refetchRecommended()] : [])
        ])
        const newOption = {
          ...site,
          label: mapToSiteFieldName(site)
        } as SiteSelectListExEntity & { label: string }
        if (mode === 'multiple') {
          const currentValues = Array.isArray(value) ? value : []
          handleChange(
            [...currentValues, site.id],
            [
              ...currentValues.map(
                (v) =>
                  options.find((o) => o.id === v) ??
                  ({} as SiteSelectListExEntity & { label: string })
              ),
              newOption
            ]
          )
        } else {
          handleChange(site.id, newOption)
        }
        setIsOpenModal(false)
        setUpdateRequest(null)
        if (onCreate) onCreate(site)
      },
      [
        handleChange,
        queryClient,
        dcId,
        refetchAll,
        refetchRecommended,
        canUseRecommendedTab,
        onCreate,
        mode,
        value,
        options
      ]
    )

    const excludeNotExistValues = useSelectExcludeNotExistValues(
      allSites,
      handleChange
    )

    const handleLinkSiteWithCustomer = useCallback(
      (site: SiteWithAreaEntity) => {
        if (!selectedCustomer) return
        if (isSiteAlreadyLinkedToCustomer(site, selectedCustomer)) return
        const resolvedSiteGroupCode = resolveSiteGroupCode(selectedCustomer)

        const customerHadNoCode =
          selectedCustomer.siteGroupCode == null ||
          selectedCustomer.siteGroupCode === ''
        const isOverwrite =
          site.siteGroupCode != null &&
          site.siteGroupCode !== resolvedSiteGroupCode

        modal.confirm({
          title: MENU.LINKING_CUSTOMER_AND_SITE,
          centered: true,
          okText: ACTIONS.LINK,
          cancelText: ACTIONS.CANCEL,
          content: (
            <>
              <div>
                {DESCRIPTIONS.LINK_SITE_WITH_SELECTED_CUSTOMER(
                  site.name,
                  selectedCustomer.name
                )}
              </div>
              {customerHadNoCode && (
                <div className="mt-1 text-xs text-neutral-400">
                  {DESCRIPTIONS.CUSTOMER_SITE_GROUP_LINK_TOOLTIP}
                </div>
              )}
              {isOverwrite && (
                <div className="mt-1 text-xs text-neutral-400">
                  {CONFIRMATION_MESSAGES.OVERWRITE_SITE_GROUP_CODE(
                    selectedCustomer.name
                  )}
                </div>
              )}
            </>
          ),
          onOk: async () => {
            try {
              await updateSiteSiteGroupCode({
                tenantKey,
                siteId: site.id,
                dto: { siteGroupCode: resolvedSiteGroupCode }
              })

              let backfillFailed = false
              if (customerHadNoCode) {
                try {
                  await updateCustomerSiteGroupCode({
                    tenantKey,
                    customerId: selectedCustomer.id,
                    dto: { siteGroupCode: resolvedSiteGroupCode }
                  })
                } catch (error) {
                  backfillFailed = true
                  toastApiErrorMessage(
                    error,
                    ERROR_MESSAGES.FAILED_TO_BACKFILL_CUSTOMER_SITE_GROUP_CODE
                  )
                }
              }

              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: sitesQueryKeys.all
                }),
                queryClient.invalidateQueries({
                  queryKey: ['sitesWithArea']
                }),
                queryClient.invalidateQueries({
                  queryKey: customersQueryKeys.all
                })
              ])
              await Promise.all([
                refetchAll(),
                ...(canUseRecommendedTab ? [refetchRecommended()] : [])
              ])
              if (!backfillFailed) {
                toast.success(SUCCESS_MESSAGES.LINKED_CUSTOMER_WITH_SITE)
              }
            } catch (error) {
              toastApiErrorMessage(
                error,
                ERROR_MESSAGES.FAILED_TO_LINK_CUSTOMER_WITH_SITE
              )
            }
          }
        })
      },
      [
        selectedCustomer,
        modal,
        tenantKey,
        queryClient,
        refetchAll,
        refetchRecommended,
        canUseRecommendedTab
      ]
    )

    return (
      <>
        <Select
          ref={ref}
          {...props}
          mode={mode}
          value={excludeNotExistValues(value)}
          loading={isFetching}
          onChange={handleChange}
          rootClassName={styles.container}
          fieldNames={{ label: 'label', value: 'id' }}
          className={clsx(['min-w-[280px]', className])}
          options={options}
          placeholder={placeholder}
          filterOption={(input, option) => {
            const inputLowerCase = input.toLowerCase()
            const labelLowerCase = (option?.label ?? '').toLowerCase()
            const locationLowerCase = (option?.location ?? '').toLowerCase()
            return (
              (labelLowerCase.toLowerCase().includes(inputLowerCase) ||
                locationLowerCase.toLowerCase().includes(inputLowerCase)) ??
              false
            )
          }}
          optionRender={(option) => {
            const isArea = option.data.isArea
            const hasNote = option.data.note?.length > 0

            const optionContent = (
              <div>
                <div className="inline-flex items-start gap-2">
                  <div className="flex-1 truncate">{option.label}</div>
                  {hasNote && (
                    <span className="mt-1.5">
                      <FaBullhorn
                        size={12}
                        className="flex-shrink-0 text-yellow"
                      />
                    </span>
                  )}
                </div>
                <div
                  className={clsx([
                    'text-xs',
                    !option.data.disabled && 'text-neutral-400'
                  ])}
                >
                  {option.data.location}&nbsp;
                </div>
              </div>
            )

            const labelContent = hasNote ? (
              <Popover
                placement="bottom"
                zIndex={1999}
                rootClassName="max-w-[400px]"
                content={
                  <div className="whitespace-pre-wrap text-sm">
                    {option.data.note}
                  </div>
                }
                {...props}
              >
                {optionContent}
              </Popover>
            ) : (
              optionContent
            )

            const canLinkCustomer =
              enableCustomerLink && !isArea && !!selectedCustomer
            // SiteSelectListExEntity（型）には siteGroupCode が無いが、実際に
            // options に流れる値は SiteWithAreaEntity（siteGroupCode 保持）
            const site = option.data as SiteWithAreaEntity

            return (
              <SelectOptionWithAction
                label={labelContent}
                truncateLabel={false}
                title=""
              >
                {(!isArea || canLinkCustomer) && (
                  <div className="flex items-center gap-1">
                    {!isArea && (
                      <Button
                        icon={<FaEdit />}
                        onClick={(e) => {
                          e.stopPropagation()
                          setUpdateRequest({
                            tenantKey: tenantKey,
                            siteId: option.data.id,
                            dto: {
                              name: option.data.name,
                              code: option.data.code,
                              location: option.data.location || '',
                              note: option.data.note || '',
                              enable: true,
                              searchAreaIds: option.data.searchAreaIds || [],
                              contractAreaId:
                                option.data.contractAreaId || null,
                              siteGroupCode: option.data.siteGroupCode ?? null,
                              // 担当者と連絡先。
                              // 渡さないとモーダルが空欄で開き、保存時に既存値を消す
                              contactName: option.data.contactName || '',
                              phoneNumber: option.data.phoneNumber || ''
                            }
                          })
                          setIsOpenModal(true)
                        }}
                      >
                        {MENU.EDITING_SITE}
                      </Button>
                    )}
                    {canLinkCustomer && (
                      <Button
                        icon={<FaLink />}
                        disabled={isSiteAlreadyLinkedToCustomer(
                          site,
                          selectedCustomer
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleLinkSiteWithCustomer(site)
                        }}
                      >
                        {ACTIONS.LINK}
                      </Button>
                    )}
                  </div>
                )}
              </SelectOptionWithAction>
            )
          }}
          dropdownRender={(menu) => (
            <div>
              {canUseRecommendedTab && (
                <>
                  <div className="px-3 pb-1 pt-2">
                    <Segmented
                      block
                      size="small"
                      value={siteTab}
                      onChange={(val) => setSiteTab(val as SiteTab)}
                      options={[
                        {
                          label: LABELS.RECOMMENDED_SITES,
                          value: SITE_TAB_RECOMMENDED
                        },
                        { label: LABELS.ALL_SITES, value: SITE_TAB_ALL }
                      ]}
                    />
                  </div>
                  <Divider className="mx-0 my-1" />
                </>
              )}
              {menu}
              <Divider className="mx-0 my-2" />
              <div className="relative flex h-8">
                <div
                  className={cn(
                    'flex items-center space-x-4 px-4',
                    siteTab === SITE_TAB_RECOMMENDED && 'invisible'
                  )}
                >
                  <Checkbox
                    checked={showSites}
                    onChange={(e) => setShowSites(e.target.checked)}
                  >
                    {LABELS.SITE_WITHOUT_AREA}
                  </Checkbox>
                  <Checkbox
                    checked={showAreas}
                    onChange={(e) => setShowAreas(e.target.checked)}
                  >
                    {LABELS.AREA}
                  </Checkbox>
                </div>
                <div className="absolute left-1/2 flex -translate-x-1/2 transform items-center px-4">
                  <Button
                    icon={<FaPlus />}
                    onClick={() => {
                      setUpdateRequest(null)
                      setIsOpenModal(true)
                    }}
                  >
                    {MENU.CREATING_SITE}
                  </Button>
                </div>
              </div>
            </div>
          )}
        />
        <SiteModal
          open={isOpenModal}
          tenantKey={tenantKey}
          dcId={dcId}
          updateRequest={updateRequest}
          customer={siteModalCustomer}
          onSuccess={handleCreateAndUpdateSuccess}
          onClose={() => setIsOpenModal(false)}
        />
        {modalContext}
      </>
    )
  }
)
